import { createAsyncThunk } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'

import { ValidationErrors } from 'store/types'
import { getAxiosInstance } from 'services/api'
import { Team, TeamDetail } from 'types/app'
import { TeamParams, CreateTeamResponse } from './types'

const api = getAxiosInstance()

export const getCaptainPanelData = createAsyncThunk(
  'team/captainPanel',
  async (teamId: string, { rejectWithValue }) => {
    try {
      // Use the correct backend route: /team/captain-panel/:id
      const response = await api.get(`/team/captain-panel/${teamId}`)
      return response.data.captainPanel
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>
      if (!error.response) throw err
      return rejectWithValue(error.response.data)
    }
  },
)

export const getAllTeams = createAsyncThunk<
  Team[],
  void,
  { rejectValue: ValidationErrors }
>('teams/getAllTeams', async (_, { rejectWithValue }) => {
  try {
    console.log('Fetching teams from /all/teams...')
    const response = await api.get('/all/teams')
    console.log('Teams response:', response.data)
    const teams = response.data.teams || []
    console.log('Returning teams:', teams.length, 'teams')
    return teams
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    console.error('Teams fetch error:', err)
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

export const handleTeamMember = createAsyncThunk<
  Team,
  { teamId: string; userId: string; isJoined: boolean },
  { rejectValue: ValidationErrors }
>(
  'teams/handleTeamMember',
  async ({ teamId, userId, isJoined }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/team/${isJoined ? 'remove' : 'add'}/member/${teamId}`,
        isJoined ? { teamMemberId: userId } : { userId: userId },
      )
      return response.data.team
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>
      if (!error.response) throw err
      return rejectWithValue(error.response.data)
    }
  },
)

export const getTeamDetails = createAsyncThunk<
  TeamDetail,
  string,
  { rejectValue: ValidationErrors }
>('teams/getTeamDetails', async (teamId, { rejectWithValue }) => {
  try {
    const response = await api.get(`/team/details/${teamId}`)
    return response.data.team
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

export const createTeam = createAsyncThunk<
  CreateTeamResponse,
  TeamParams,
  { rejectValue: ValidationErrors }
>(
  'team/create',
  async (
    {
      avatar,
      team_name,
      team_url,
      team_description,
      game_id,
      cover_photo,
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new FormData()
      if (avatar) params.append('avatar', avatar)
      if (cover_photo) params.append('cover_photo', cover_photo)
      if (team_name) params.append('team_name', team_name)
      if (team_url) params.append('team_url', team_url)
      if (game_id) params.append('game_id', game_id)
      if (team_description) params.append('team_description', team_description)

      const response = await api.post('/create/team', params, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })

      return response.data
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>
      if (!error.response) throw err
      return rejectWithValue(error.response.data)
    }
  },
)

export const followTeam = createAsyncThunk<
  Team,
  string,
  { rejectValue: ValidationErrors }
>('team/follow', async (teamId, { rejectWithValue }) => {
  try {
    const response = await api.put(`/team/follow/${teamId}`)
    return response.data.team
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

export const updateTeam = createAsyncThunk<
  { Team: Team; message: string },
  { teamId: string; team: TeamParams },
  { rejectValue: ValidationErrors }
>('team/update', async ({ teamId, team }, { rejectWithValue }) => {
  try {
    const params = new FormData()
    if (team.avatar) params.append('avatar', team.avatar)
    if (team.cover_photo) params.append('cover_photo', team.cover_photo)
    if (team.team_name) params.append('team_name', team.team_name)
    if (team.team_url) params.append('team_url', team.team_url)
    if (team.game_id) params.append('game_id', team.game_id)
    if (team.team_description)
      params.append('team_description', team.team_description)

    const response = await api.put(`/team/${teamId}`, params, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })

    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// Request to join a team
export const requestToJoinTeam = createAsyncThunk<
  { message: string; success: boolean },
  { teamId: string; message: string },
  { rejectValue: ValidationErrors }
>('team/requestToJoin', async ({ teamId, message }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/team/join/${teamId}`, { message })
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// Delete team - Uses existing backend endpoint
export const deleteTeam = createAsyncThunk<
  { message: string; success: boolean },
  string,
  { rejectValue: ValidationErrors }
>('team/delete', async (teamId, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/team/${teamId}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// Admin: delete any team (cleanup). Backend refuses (409) while the team is in an active tournament/league.
export const adminDeleteTeam = createAsyncThunk<
  { message: string; success: boolean },
  string,
  { rejectValue: ValidationErrors }
>('team/adminDelete', async (teamId, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/admin/team/${teamId}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})

// Leave team - This endpoint should already exist from your backend code
export const leaveTeam = createAsyncThunk<
  { message: string; success: boolean; teamDeleted?: boolean },
  { teamId: string; options: { newOwnerId?: string; disband?: boolean } },
  { rejectValue: ValidationErrors }
>('team/leave', async ({ teamId, options }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/team/leave/${teamId}`, options)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>
    if (!error.response) throw err
    return rejectWithValue(error.response.data)
  }
})
